import { IonButton, IonInput, IonRouterLink } from '@ionic/react';
import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import { Icon } from '@/components/memo';
import { useGetStarted } from '@/contexts';
import { useInputFocus, useToast } from '@/hooks';
import {
  GET_STARTED_STEPS,
  OTP_EXPIRY_IN_SECONDS,
  OTP_LENGTH
} from '@/utils/constants';
import { arrowForwardCircleOutline } from 'ionicons/icons';

import './Verification.css';

const Verification: React.FC = () => {
  const {
    mobileNumber,
    resendOtp,
    verifyOtp,
    setCurrentStep,
    isResendDisabled,
    setIsResendDisabled
  } = useGetStarted();
  const displayToast = useToast();

  const [otp, setOtp] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(OTP_EXPIRY_IN_SECONDS);

  const inputRef = useRef<HTMLIonInputElement>(null);

  useInputFocus(inputRef);

  const copy = {
    subHeader: 'VERIFY YOUR MOBILE NUMBER',
    header: (
      <>
        A verification <span>code</span> has been sent to:
      </>
    ),
    buttonText: 'Next'
  };

  const formattedMobileNumber = mobileNumber
    ? `0${mobileNumber.slice(0, 3)} ${mobileNumber.slice(3, 6)} ${mobileNumber.slice(6, 10)}`
    : '--';

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setIsResendDisabled(false);
    }
  }, [timer]);

  const onInput = (event: CustomEvent) => {
    setOtp(event.detail.value!);
    setHasError(false);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNext();
    }
  };

  const onNext = useCallback(() => {
    if (otp.length === OTP_LENGTH) {
      setHasError(false);
      verifyOtp(otp);
    } else {
      setHasError(true);
      displayToast({
        message: 'Please enter a valid OTP.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  }, [otp, verifyOtp]);

  const onResend = useCallback(() => {
    if (!isResendDisabled) {
      resendOtp();
      setTimer(OTP_EXPIRY_IN_SECONDS);
      setOtp('');
      setHasError(false);
    }
  }, [resendOtp, isResendDisabled]);

  return (
    <div className="verification">
      <Spacer top={32} bottom={24}>
        <h4 className="verification__sub-header">{copy.subHeader}</h4>
        <h1 className="verification__header">{copy.header}</h1>
      </Spacer>
      <div className="verification__number">
        <h2 className="verification__mobile-number">{formattedMobileNumber}</h2>
        <IonRouterLink
          onClick={() => setCurrentStep(GET_STARTED_STEPS.MOBILE_NUMBER)}
          className="verification__change-number"
        >
          Change Number
        </IonRouterLink>
      </div>

      <Spacer top={16} bottom={102}>
        <div className="verification__form">
          <div
            className={`verification__input ${hasError ? 'verification__input--error' : ''}`}
          >
            <IonInput
              ref={inputRef}
              type="text"
              inputmode="numeric"
              label="Verification Code"
              labelPlacement="stacked"
              value={otp}
              placeholder="Enter verification code"
              onIonInput={onInput}
              onKeyDown={onKeyDown}
              maxlength={6}
              autoFocus={true}
            />
          </div>
          <p className="verification__resend-otp">
            Didn't receive a code?{' '}
            <IonRouterLink
              onClick={onResend}
              className={isResendDisabled ? 'disabled' : ''}
              color={isResendDisabled ? 'medium' : 'primary'}
            >
              Resend {isResendDisabled && timer > 0 && ` in ${timer}s`}
            </IonRouterLink>
          </p>
        </div>
      </Spacer>
      <ButtonContainer>
        <IonButton
          id="next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <Icon slot="start" icon={arrowForwardCircleOutline} />
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default Verification;
